"use client";

import { CheckCircle2, MinusCircle, PlusCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import type { RunRecord } from "@/lib/aicheck/history/types";
import { authService } from "@/services/auth";
import type { OutputLanguage } from "@/types/aicheck";

interface DiffCounts {
  newCount: number;
  continuedCount: number;
  resolvedCount: number;
}

/**
 * U6: 再実行後の「解消 / 新規 / 継続」確定値サマリを結果ヘッダに常設表示する。
 * - 数値は履歴 API（persistRunResult が算出）の権威データのみを使う。ローカル LLM 不在でも出る。
 * - 前回比較が無い（初回実行 / 履歴取得不可）ときは何も描画しない。
 * - クリックで「差分・履歴」タブへ遷移（onJump がある場合のみ。無ければ非クリック）。
 */
export function RunDiffSummaryBadges({
  projectId,
  runId,
  language,
  onJump,
}: {
  projectId: number;
  runId?: string;
  language: OutputLanguage;
  onJump?: () => void;
}) {
  const [counts, setCounts] = useState<DiffCounts | null>(null);

  useEffect(() => {
    if (!(projectId > 0)) return;
    let cancelled = false;
    setCounts(null);

    void (async () => {
      try {
        const token = await authService.getIdToken();
        if (cancelled || !token) return;
        const res = await fetch(
          apiUrl(
            `/api/aicheck/projects/${encodeURIComponent(String(projectId))}`,
          ),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled || !res.ok) return;
        const data = (await res.json()) as { runs?: RunRecord[] };
        const runs = data.runs ?? [];

        // 同一言語・完了・非 freeform の run のみが diff 連鎖の対象（store 側の算出条件に合わせる）。
        // 一覧は started_at 降順（新しい順）で返る。
        const chain = runs.filter(
          (r) =>
            r.language === language &&
            r.status === "completed" &&
            r.trigger !== "freeform",
        );

        // 表示中の run を特定：runId 一致優先、無ければ最新（＝直近完了）を採る。
        const target =
          (runId ? chain.find((r) => r.runId === runId) : undefined) ??
          chain[0];
        if (!target) return;

        // 前回比較が無い（初回実行）なら表示しない。
        const hasPrevious = chain.some(
          (r) => r.runId !== target.runId && r.startedAt < target.startedAt,
        );
        if (!hasPrevious) return;

        if (!cancelled) {
          setCounts({
            newCount: target.newCount,
            continuedCount: target.continuedCount,
            resolvedCount: target.resolvedCount,
          });
        }
      } catch {
        // 付加表示。取得失敗は静かに無表示。
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, runId, language]);

  if (!counts) return null;

  const body = (
    <>
      <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
        前回比較
      </span>
      <span
        className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700"
        title={`解消された指摘 ${counts.resolvedCount} 件`}
      >
        <CheckCircle2 size={13} />
        解消 {counts.resolvedCount}
      </span>
      <span
        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"
        title={`新規の指摘 ${counts.newCount} 件`}
      >
        <PlusCircle size={13} />
        新規 {counts.newCount}
      </span>
      <span
        className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-semibold text-zinc-600"
        title={`前回から継続中の指摘 ${counts.continuedCount} 件`}
      >
        <MinusCircle size={13} />
        継続 {counts.continuedCount}
      </span>
    </>
  );

  if (onJump) {
    return (
      <button
        type="button"
        onClick={onJump}
        className="flex w-full flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-gray-50"
        title="差分・履歴タブを開く"
      >
        {body}
        <span className="ml-auto text-[11px] font-medium text-indigo-600">
          差分・履歴 →
        </span>
      </button>
    );
  }

  return (
    <div className="flex w-full flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5">
      {body}
    </div>
  );
}
